import { LaboratoryPermissionType } from "@/contracts/permissions";
import { LabTestStatus, ProcessingTestStatus } from "@/models";

export interface IPermissionService {
  readonly canCreateAnyTest: boolean;
  readonly hasAnyLabAccess: boolean;
  readonly isLoaded: boolean;
  canModifyTest(laboratoryId: string, testStatus: LabTestStatus): boolean;
  canModifyProcessingTest(laboratoryId: string, processingTestStatus: ProcessingTestStatus): boolean;
  canApproveProcessingTest(laboratoryId: string, processingTestStatus: ProcessingTestStatus): boolean;
  canCreateTest(laboratoryId: string): boolean;
  canRunReports(laboratoryId: string): boolean;
  canManageUsers(laboratoryId: string): boolean;
  canAccessLab(laboratoryId: string): boolean;
}

export class PermissionService implements IPermissionService {
  readonly canCreateAnyTest: boolean;
  readonly hasAnyLabAccess: boolean;

  constructor(
    private readonly isUserAdmin: boolean,
    private readonly laboratoryPermissions: Map<string, Set<LaboratoryPermissionType>>,
    readonly isLoaded: boolean,
  ) {
    this.canCreateAnyTest = [...laboratoryPermissions.values()].some((p) =>
      p.has(LaboratoryPermissionType.CreateTests),
    );
    this.hasAnyLabAccess = [...laboratoryPermissions.values()].some((p) => p.size > 0);
  }

  private hasLabPermission(laboratoryId: string, permission: LaboratoryPermissionType): boolean {
    return this.laboratoryPermissions.get(laboratoryId)?.has(permission) ?? false;
  }

  canModifyTest(laboratoryId: string, testStatus: LabTestStatus): boolean {
    if (testStatus === LabTestStatus.ReadyForTesting || testStatus === LabTestStatus.InTesting) {
      return this.hasLabPermission(laboratoryId, LaboratoryPermissionType.PerformTests);
    }
    return false;
  }

  canModifyProcessingTest(laboratoryId: string, processingTestStatus: ProcessingTestStatus): boolean {
    if (
      processingTestStatus === ProcessingTestStatus.ReadyForProcessing ||
      processingTestStatus === ProcessingTestStatus.InProcessing
    ) {
      return this.hasLabPermission(laboratoryId, LaboratoryPermissionType.ProcessTests);
    }
    if (
      processingTestStatus === ProcessingTestStatus.ReadyForApproval ||
      processingTestStatus === ProcessingTestStatus.InApproval
    ) {
      return this.canApproveProcessingTest(laboratoryId, processingTestStatus);
    }
    return false;
  }

  canApproveProcessingTest(laboratoryId: string, processingTestStatus: ProcessingTestStatus): boolean {
    if (
      processingTestStatus !== ProcessingTestStatus.ReadyForApproval &&
      processingTestStatus !== ProcessingTestStatus.InApproval
    ) {
      return false;
    }
    return this.hasLabPermission(laboratoryId, LaboratoryPermissionType.ApproveTests);
  }

  canCreateTest(laboratoryId: string): boolean {
    return this.hasLabPermission(laboratoryId, LaboratoryPermissionType.CreateTests);
  }

  canRunReports(laboratoryId: string): boolean {
    return (
      this.hasLabPermission(laboratoryId, LaboratoryPermissionType.ProcessTests) ||
      this.hasLabPermission(laboratoryId, LaboratoryPermissionType.ApproveTests)
    );
  }

  canManageUsers(laboratoryId: string): boolean {
    return this.isUserAdmin || this.hasLabPermission(laboratoryId, LaboratoryPermissionType.ManageUsers);
  }

  canAccessLab(laboratoryId: string): boolean {
    return (this.laboratoryPermissions.get(laboratoryId)?.size ?? 0) > 0;
  }
}

export class AllowAllPermissionService implements IPermissionService {
  readonly canCreateAnyTest = true;
  readonly hasAnyLabAccess = true;
  readonly isLoaded = true;

  canModifyTest(_laboratoryId: string, _testStatus: LabTestStatus): boolean {
    return true;
  }

  canModifyProcessingTest(_laboratoryId: string, _processingTestStatus: ProcessingTestStatus): boolean {
    return true;
  }

  canApproveProcessingTest(_laboratoryId: string, _processingTestStatus: ProcessingTestStatus): boolean {
    return true;
  }

  canCreateTest(_laboratoryId: string): boolean {
    return true;
  }

  canRunReports(_laboratoryId: string): boolean {
    return true;
  }

  canManageUsers(_laboratoryId: string): boolean {
    return true;
  }

  canAccessLab(_laboratoryId: string): boolean {
    return true;
  }
}
